import React, { useEffect, useState } from "react";
import {
  courseListingFields,
  majorOptions,
  stateOptions,
} from "../../formSchemas/courseFormSchemas";
import { FieldConfig, FormProps } from "./FormComponent/types";
import TagEditableList from "./FormComponent/TagEditableList";
import ObjectListEditor from "./FormComponent/ObjectListEditor";
import { db } from "../../firebase";
import { getDocs, collection } from "firebase/firestore";

interface CourseFormProps extends FormProps {
  initialData?: Record<string, any>;
  disabled?: boolean;
}

const CourseForm: React.FC<CourseFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  disabled = false,
}) => {
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [universityData, setUniversityData] = useState<
    { universityName: string; country?: string }[]
  >([]);
  const isEdit = !!initialData?.id;

  // ✅ 1️⃣ Sync formData whenever initialData changes
  useEffect(() => {
    if (initialData) {
      const normalizedData = { ...initialData };

      if (Array.isArray(normalizedData.scholarships)) {
        normalizedData.scholarships = normalizedData.scholarships.map((s) => {
          if (typeof s === "string") {
            const match = s.match(/^(.*?)(?:\s*\(\$?(\d+(?:\.\d+)?)\))?$/);
            return {
              title: match?.[1]?.trim() || s,
              amount: match?.[2] ? Number(match[2]) : "",
            };
          }
          return s;
        });
      }

      setFormData({
        ...normalizedData,
        fieldOfStudy: normalizedData.fieldOfStudy || "",
        country: normalizedData.country || "",
      });
    } else {
      setFormData({});
    }
  }, [initialData]);

  // ✅ Fetch university data (name + country), sorted ascending
  useEffect(() => {
    const fetchUniversityData = async () => {
      try {
        const snapshot = await getDocs(collection(db, "university-listing"));
        const data = snapshot.docs
          .map((doc) => ({
            universityName: doc.data().universityName,
            country: doc.data().country,
          }))
          .filter((u) => u.universityName)
          .sort((a, b) =>
            a.universityName
              .toLowerCase()
              .localeCompare(b.universityName.toLowerCase())
          );

        setUniversityData(data);

        const universityNames = data.map((u) => u.universityName);

        const updatedFields = courseListingFields.map((field) =>
          field.name === "universityName"
            ? { ...field, options: universityNames }
            : field
        );

        setFields(updatedFields);
      } catch (err) {
        console.error("Error fetching university data:", err);
        setFields(courseListingFields);
      }
    };

    fetchUniversityData();
  }, []);

  // ✅ When universityName changes, auto-set the corresponding country
  useEffect(() => {
    if (formData.universityName && universityData.length > 0) {
      const selectedUni = universityData.find(
        (u) => u.universityName === formData.universityName
      );
      if (selectedUni?.country && selectedUni.country !== formData.country) {
        setFormData((prev) => ({
          ...prev,
          country: selectedUni.country,
        }));
      }
    }
  }, [formData.universityName, universityData]);

  // ✅ 3️⃣ Update dependent dropdowns (major & state)
  useEffect(() => {
    if (!formData) return;

    setFields((prevFields) =>
      prevFields.map((field) => {
        if (field.name === "major") {
          const selectedField =
            formData.fieldOfStudy || initialData?.fieldOfStudy;
          return {
            ...field,
            options: selectedField ? majorOptions[selectedField] || [] : [],
          };
        }

        if (field.name === "state") {
          const selectedCountry = formData.country || initialData?.country;
          return {
            ...field,
            options: selectedCountry ? stateOptions[selectedCountry] || [] : [],
          };
        }

        // Keep other fields (like universityName) intact
        return field;
      })
    );
  }, [formData.fieldOfStudy, formData.country, initialData]);

  // ✅ Handle input value change
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ✅ Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.values(errors).some(Boolean)) return;

    try {
      if (onSubmit) await onSubmit(formData);
    } catch (err: any) {
      console.error("Course form submit error:", err);
      alert(err.message || "Failed to submit course form.");
    }
  };

  // ✅ Safely handle all field values (avoids object rendering error)
  const getSafeValue = (fieldName: string): string => {
    const value = formData[fieldName];
    if (Array.isArray(value)) {
      if (value.every((v) => typeof v === "object" && v !== null)) {
        return value.map((v) => v.title || JSON.stringify(v)).join(", ");
      }
      return value.join(", ");
    }
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value);
    }
    return value ?? "";
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 overflow-auto">
      <div
        className="grid gap-4"
        // className={`grid gap-4 ${
        //   fields.length > 8 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
        // }`}
      >
        {fields.map((field) => {
          // const widthClass = fields.length > 8 ? "w-full" : "w-60";
          const widthClass = "w-2/3";

          const isCountryField = field.name === "country";

          return (
            <div key={field.name} className="flex w-full">
              <label
                htmlFor={field.name}
                className="w-60 pt-2 text-gray-800 font-medium"
              >
                {field.label}:
              </label>

              {/* Tags Field */}
              {field.type === "tags" ? (
                (() => {
                  const addTagKey = `add-${field.name}`;
                  const addTagValue = formData[addTagKey] || "";

                  const handleAddTag = (newTag: string) => {
                    if (
                      newTag &&
                      !(formData[field.name] || []).includes(newTag)
                    ) {
                      setFormData({
                        ...formData,
                        [field.name]: [...(formData[field.name] || []), newTag],
                        [addTagKey]: "",
                      });
                    }
                  };

                  return (
                    // <div className={`${fields.length > 8 ? "w-full" : "w-80"}`}>
                    <div className="w-2/3">
                      {field.options && field.options.length > 0 ? (
                        <select
                          value=""
                          onChange={(e) => handleAddTag(e.target.value)}
                          disabled={disabled || field.disabled}
                          className="w-full p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100"
                        >
                          <option value="">Select {field.label}</option>
                          {field.options
                            .filter(
                              (opt) =>
                                !(formData[field.name] || []).includes(opt)
                            )
                            .map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                        </select>
                      ) : (
                        <div className="flex gap-2 items-center">
                          <input
                            type="text"
                            name={addTagKey}
                            value={addTagValue}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                [addTagKey]: e.target.value,
                              })
                            }
                            placeholder={
                              field.placeholder || `Add ${field.label}`
                            }
                            disabled={disabled || field.disabled}
                            className="flex-1 p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && addTagValue.trim()) {
                                e.preventDefault();
                                handleAddTag(addTagValue.trim());
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (addTagValue.trim()) {
                                handleAddTag(addTagValue.trim());
                              }
                            }}
                            disabled={
                              disabled ||
                              field.disabled ||
                              !addTagValue.trim() ||
                              (formData[field.name] || []).includes(
                                addTagValue.trim()
                              )
                            }
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            Add
                          </button>
                        </div>
                      )}

                      {(formData[field.name] || []).length > 0 && (
                        <TagEditableList
                          tags={formData[field.name] || []}
                          fieldName={field.name}
                          setFormData={setFormData}
                          formData={formData}
                          disabled={disabled}
                          allowTagEdit={field.allowTagEdit ?? true}
                        />
                      )}
                    </div>
                  );
                })()
              ) : field.type === "objectList" ? (
                <div className={`${widthClass}`}>
                  <ObjectListEditor
                    label={field.label}
                    items={
                      Array.isArray(formData[field.name])
                        ? formData[field.name]
                        : []
                    }
                    onChange={(updatedList) =>
                      setFormData({ ...formData, [field.name]: updatedList })
                    }
                    disabled={disabled || field.disabled}
                    objectFields={field.objectFields || []}
                    widthClass={widthClass}
                  />
                </div>
              ) : field.type === "textarea" ? (
                <textarea
                  name={field.name}
                  value={getSafeValue(field.name)}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  disabled={disabled || field.disabled}
                  rows={4}
                  className={`${widthClass} p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
                />
              ) : field.type === "select" ? (
                <select
                  name={field.name}
                  value={getSafeValue(field.name)}
                  onChange={handleChange}
                  disabled={
                    disabled ||
                    field.disabled ||
                    (field.name === "major" && !formData.fieldOfStudy) ||
                    (field.name === "state" && !formData.country) ||
                    isCountryField
                  }
                  required={field.required}
                  className={`${widthClass} p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100 h-9`}
                >
                  <option value="">Select</option>
                  {field.options?.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  name={field.name}
                  value={getSafeValue(field.name)}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  disabled={disabled || field.disabled}
                  required={field.required}
                  className={`${widthClass} p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100 h-9`}
                />
              )}

              {errors[field.name] && (
                <p className="text-sm text-red-600 ml-4 mt-1">
                  {errors[field.name]}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Buttons */}
      <div className="flex gap-4 mt-2">
        <button
          type="submit"
          disabled={disabled || Object.values(errors).some(Boolean)}
          className="w-44 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
        >
          Save Course
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="w-44 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default CourseForm;
